import { WORDS, RETIRED_WORDS } from './pseudonymWords';

/**
 * Replace real names with pseudonyms while keeping the roster in the same
 * alphabetical order.
 *
 * WHY ORDER MATTERS: a teacher cross-references the gradebook against a paper
 * list, an SIS export, a seating plan. A roster that sorts differently from
 * every other list they hold is worse than one with real names on it, because
 * they will stop using it.
 *
 * THE HARD PART IS THE LATE JOINER. Sanitising is irreversible — the real names
 * are gone — so when a student joins two weeks later there is nothing left to
 * sort them against. Evenly spacing the first batch is not enough on its own,
 * because we would not know WHICH gap the newcomer belongs in.
 *
 * So position is derived from the name ITSELF, by a monotone map: a name is
 * turned into a fraction of the way through its letter, and that picks a target
 * index in the word list. The map depends only on the one name being placed, so
 * a student sanitised today and one sanitised in October land in the right
 * relative positions without either knowing about the other. Gaps exist because
 * names cluster unevenly, and that is exactly where late arrivals slot in.
 */

/** Letters after the first that carry ordering information. Three is plenty:
 *  it separates Smith from Smyth, and a fourth adds precision nobody can see. */
const DEPTH = 3;

/**
 * Where this name sits within its letter, as a fraction in [0, 1).
 *
 * Base-27 over the next DEPTH letters: absent letters sort before 'a', so
 * "Sm" precedes "Sma", which is how a dictionary orders them. Non-letters are
 * folded to the low end rather than rejected — an apostrophe or a hyphen must
 * not move O'Brien to the far end of the O group.
 */
export function namePosition(name: string): number {
  const rest = name.trim().toLowerCase().slice(1);
  let value = 0;
  let scale = 1;
  for (let i = 0; i < DEPTH; i++) {
    const code = rest.charCodeAt(i);
    // 0 = "no letter here", 1..26 = a..z. Anything else (digit, dash, accent)
    // reads as 0 so it never dominates the ordering.
    const slot = Number.isNaN(code) ? 0 : (code >= 97 && code <= 122 ? code - 96 : 0);
    scale /= 27;
    value += slot * scale;
  }
  return value;
}

/**
 * Letters that do not decompose under NFD but have an obvious Latin bucket.
 * Ø sorting into the A group put Ødegaard at the top of a roster instead of
 * between Nguyen and Olsen.
 */
const FOLD: Record<string, string> = {
  'Ø': 'O', 'Œ': 'O', 'Æ': 'A', 'Å': 'A', 'Ł': 'L', 'Đ': 'D', 'Ð': 'D', 'Þ': 'T', 'ẞ': 'S', 'İ': 'I', 'I': 'I',
};

/** Strip accents so É files under E, and fold the letters NFD leaves alone. */
export function foldLetter(ch: string): string {
  const up = ch.toUpperCase();
  if (FOLD[up]) return FOLD[up];
  const stripped = up.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const c = stripped[0] || '';
  return c >= 'A' && c <= 'Z' ? c : '';
}

/**
 * The letter bucket a name belongs to, or '' when the script has no Latin
 * equivalent.
 *
 * A CJK or Arabic surname genuinely has no place in an A-Z word list, and
 * quietly filing every one of them under 'A' would put them in an arbitrary
 * block at the top of the roster — order destroyed, silently. Callers get ''
 * and must surface it rather than guess.
 */
export function letterOf(name: string): string {
  return foldLetter(name.trim()[0] || '');
}

/**
 * Is this word one we issue as a placeholder?
 *
 * A SECONDARY signal only. `placeholderName` on the row is the primary one, but it was added after
 * the import path already existed, so rows written before it carry nothing — and for those, both
 * names being words from our own pools is the best evidence available.
 *
 * The pools hold objects, materials, measurements and adjectives and no personal names at all (every
 * one was removed deliberately: a placeholder that reads as a name is indistinguishable from the
 * thing it replaces). So a real roster having BOTH a first and last name drawn from them is not a
 * case worth designing around, while a placeholder we issued always does.
 */
export function isPseudonymWord(word: string): boolean {
  return inPool(word, WORDS) || inPool(word, RETIRED_WORDS);
}

/**
 * Is this a word we would issue TODAY?
 *
 * Separate from `isPseudonymWord`, and the distinction is load-bearing.
 *
 * `RETIRED_WORDS` is the pool that was thrown out BECAUSE it was full of personal names — Bailey,
 * Jackson, Madison, Jordan, Hazel, Xavier, Victoria, Palmer, Nelson, Thatcher. Feeding it back into
 * the recogniser made it possible for a real student's real name to read as a placeholder: with 69
 * plausible first names and 74 plausible surnames in there, a child called Jordan Palmer satisfied
 * "both names are words from our own pools". The conversion then left their real name on the
 * classmate-readable document, stamped it `placeholderName: true` so nothing would ever flag it
 * again, and the banner went green.
 *
 * So the retired pool is CORROBORATING evidence only. A row whose names are retired words is a
 * placeholder when the vault also holds a different name for that student; on its own it proves
 * nothing, because the words are ordinary English names. The current pool — objects, materials and
 * measurements, with every personal name deliberately removed — is strong enough to stand alone.
 */
export function isIssuedWord(word: string): boolean {
  return inPool(word, WORDS);
}

const inPool = (word: string, pool: Record<string, readonly string[]>): boolean => {
  const w = (word || '').trim();
  if (!w) return false;
  const bucket = pool[foldLetter(w[0]).toUpperCase()];
  if (!bucket) return false;
  const lower = w.toLowerCase();
  return bucket.some(x => x.toLowerCase() === lower);
};

export interface Nameable {
  id: string;
  firstName?: string;
  lastName?: string;
}

export interface Pseudonym {
  firstName: string;
  lastName: string;
}

/** Word already in use for a letter, by index, so a re-run never reissues one. */
type Taken = Map<string, Set<number>>;

const takenIndex = (word: string): number => {
  const list = WORDS[letterOf(word)];
  return list ? list.indexOf(word) : -1;
};

function buildTaken(existing: Iterable<string>): Taken {
  const taken: Taken = new Map();
  for (const w of existing) {
    const letter = letterOf(w);
    const idx = takenIndex(w);
    if (idx < 0) continue;
    if (!taken.has(letter)) taken.set(letter, new Set());
    taken.get(letter)!.add(idx);
  }
  return taken;
}

/**
 * Claim the free index closest to `target`, never below `floor`.
 *
 * `floor` is what preserves order within one batch: each student must land
 * strictly after the previous one, so two names that map to the same target
 * cannot swap. Searching outward from the target (rather than always upward)
 * keeps clusters near where they belong instead of pushing a whole run to the
 * end of the list.
 */
function claim(
  taken: Taken,
  letter: string,
  target: number,
  floor: number,
  /**
   * Real names on this roster, lowercased. A candidate equal to ANY of them is skipped.
   *
   * ── WHY THIS EXISTS, AND WHY THE WORD LIST IS NOT ENOUGH ────────────────────────────────────
   *
   * A pseudonym is a deterministic function of the real name, so a word that happens to BE a name can
   * be issued to the very child it belongs to. The old vocabulary was chosen to read like names, and
   * measured 43 words out of 905 mapping to themselves — a student called Hazel, Briar, Dahlia or
   * Cadence was handed her own real name as her placeholder and it went straight onto the
   * classmate-readable class document.
   *
   * The word list is now objects and adjectives, which makes that rare. This makes it IMPOSSIBLE,
   * which matters because no word list can be proven free of every name in every language — and
   * because the second collision is just as damaging and far less obvious: one child's placeholder
   * matching a CLASSMATE's real name. That made the leak guard refuse legitimate whole-roster saves,
   * and on class delete it discarded the history record, losing the marks and attendance with it.
   *
   * Checked against the whole roster rather than the one child, because "Dahlia Smith" being issued
   * "Daisy Dahlia" is the same disclosure from the classmate's point of view.
   */
  realNames: ReadonlySet<string>,
): number | null {
  const list = WORDS[letter];
  if (!list || !list.length) return null;
  const used = taken.get(letter) || new Set<number>();
  const start = Math.min(Math.max(Math.round(target), floor), list.length - 1);
  for (let d = 0; d < list.length; d++) {
    for (const idx of d === 0 ? [start] : [start + d, start - d]) {
      if (idx < floor || idx >= list.length) continue;
      if (used.has(idx)) continue;
      // A word that is somebody's actual name is not a pseudonym. Take the next one.
      if (realNames.has(list[idx].toLowerCase())) continue;
      if (!taken.has(letter)) taken.set(letter, used);
      used.add(idx);
      return idx;
    }
  }
  return null;
}

export interface AssignOptions {
  /** Pseudonyms already issued in this class, so a second run never collides. */
  alreadyUsed?: { firstNames?: Iterable<string>; lastNames?: Iterable<string> };
}

export interface AssignResult {
  /** studentId → the pseudonym to write. Students already sanitised are absent. */
  assignments: Map<string, Pseudonym>;
  /** Students no word could be found for — reported, never silently skipped. */
  exhausted: string[];
}

/**
 * Assign pseudonyms to `students`, preserving their alphabetical order.
 *
 * Ordering is carried entirely by the LAST name, which is what a roster sorts
 * on. Words are unique, so no two students tie and the first name never has to
 * break one — it is assigned independently on its own letter.
 */
export function assignPseudonyms(students: Nameable[], opts: AssignOptions = {}): AssignResult {
  /**
   * ONE pool of words for the whole class, not one per name slot.
   *
   * Separate pools let the same word be a surname for one student and a first
   * name for another — a seventeen-student roster produced "Magnet Archer"
   * beside "Archer Astral", and "Talisman Talbot" beside "Thistle Talisman".
   * Three collisions in seventeen names. It is defensible and it reads as a
   * mistake, which for a feature whose whole job is to be trusted is the same
   * thing.
   *
   * The cost is capacity: a student whose first and last names share a letter
   * now spends two words from it. Forty words still covers twenty such students
   * per letter, and anything beyond that is reported through `exhausted` rather
   * than silently reusing a word.
   */
  /**
   * Every real name on the roster, first and last, lowercased — the set no pseudonym may equal.
   *
   * Built from the WHOLE batch before any assignment, so the first student assigned is protected from
   * the last student's surname just as the last is from the first's.
   */
  const realNames = new Set<string>();
  for (const s of students) {
    const f = (s.firstName || '').trim().toLowerCase();
    const l = (s.lastName || '').trim().toLowerCase();
    if (f) realNames.add(f);
    if (l) realNames.add(l);
  }

  const taken = buildTaken([
    ...(opts.alreadyUsed?.lastNames || []),
    ...(opts.alreadyUsed?.firstNames || []),
  ]);
  const takenLast = taken;
  const takenFirst = taken;

  // Sort by the real name so the batch is walked in the order it must end up in.
  const ordered = [...students].sort((a, b) =>
    `${a.lastName || ''} ${a.firstName || ''}`.localeCompare(`${b.lastName || ''} ${b.firstName || ''}`, 'en'));

  const assignments = new Map<string, Pseudonym>();
  const exhausted: string[] = [];
  // Per letter, the highest index issued in THIS batch — the order floor.
  const floors = new Map<string, number>();

  for (const s of ordered) {
    const lastReal = (s.lastName || '').trim() || (s.firstName || '').trim();
    if (!lastReal) { exhausted.push(s.id); continue; }

    const lastLetter = letterOf(lastReal);
    // No Latin bucket — a name in a script this vocabulary cannot represent.
    // Reported, never filed under 'A' as if it belonged there.
    if (!lastLetter) { exhausted.push(s.id); continue; }
    const lastList = WORDS[lastLetter] || [];
    const floor = (floors.get(lastLetter) ?? -1) + 1;
    const lastIdx = claim(takenLast, lastLetter, namePosition(lastReal) * lastList.length, floor, realNames);
    if (lastIdx === null) { exhausted.push(s.id); continue; }
    floors.set(lastLetter, lastIdx);

    // First names carry no ordering duty, so they need no floor — only a letter
    // and a free slot near their own position.
    const firstReal = (s.firstName || '').trim() || lastReal;
    const firstLetter = letterOf(firstReal) || lastLetter;
    const firstList = WORDS[firstLetter] || [];
    let firstIdx = claim(takenFirst, firstLetter, namePosition(firstReal) * firstList.length, 0, realNames);
    // "Sagebrush Sagebrush" — both names drawing the same word from the same
    // letter reads as a joke and costs the teacher's trust in the whole
    // feature. Claim again; the first index stays taken so we never loop.
    while (firstIdx !== null && firstList[firstIdx] === lastList[lastIdx]) {
      firstIdx = claim(takenFirst, firstLetter, firstIdx + 1, 0, realNames);
    }
    if (firstIdx === null) { exhausted.push(s.id); continue; }

    assignments.set(s.id, {
      firstName: firstList[firstIdx],
      lastName: lastList[lastIdx],
    });
  }

  return { assignments, exhausted };
}
